// Tipi allineati al backend
import type { CreateSupplierDto } from '@shared/dto/supplier/create-supplier.dto';

export type { CreateSupplierDto };

export interface Supplier {
  id: string;
  name: string;
  vatNumber: string;
  address?: string;
  email?: string;
  phone?: string;
}

export interface DeliveryNotePhoto {
  id: string;
  storageKey: string;
  originalFilename: string;
  sourceKey?: string;
}

export interface LabelPhoto {
  id: string;
  storageKey: string;
  originalFilename: string;
}

export interface DeliveryItemAllergen {
  allergenId: string;
  allergenCode: string;
  allergenName: string;
  category: 'CONTAINS' | 'MAY_CONTAIN';
}

export interface DeliveryItem {
  id: string;
  productName: string;
  batchNumber: string | null;
  expiryDate: string | null;
  /** True for shelf-stable products without HACCP expiry — server-authored flag. */
  hasNoExpiry: boolean;
  quantity: number;
  unit: string;
  status: 'PENDING' | 'ACCEPTED' | 'ACCEPTED_WITH_RESERVATION' | 'REJECTED';
  statusReason: string | null;
  isCompliant: boolean;
  labelPhotos: LabelPhoto[];
  allergens: DeliveryItemAllergen[];
  unitPrice?: number | null;
  lineTotal?: number | null;
  currency?: string | null;
  pastPurchaseCount?: number;
  lastPurchaseDate?: string | null;
}

interface MatchingLot {
  id: string;
  name: string;
  lotNumber: string;
  notes: string | null;
}

type ExtractionStatus = 'IDLE' | 'PENDING' | 'COMPLETED' | 'FAILED';

interface InferredAllergenEntry {
  allergenCode: string;
  category: 'CONTAINS' | 'MAY_CONTAIN';
}

/**
 * A company extracted from the DDT (PRSP-193). Local passthrough type —
 * mirrors backend's `SupplierMatchCandidate` (delivery.service.ts), never
 * imported from `shared/`: the wire payload lives in `extractionResult`
 * (`unknown` on the DTO), untyped by convention.
 */
export interface ExtractedSupplierCandidate {
  name: string | null;
  vatNumber: string | null;
  role: 'issuer' | 'recipient' | 'carrier' | 'unknown';
  sourceHint: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  isOwnCompany: boolean;
  existingSupplier: {
    id: string;
    name: string;
    vatNumber: string;
  } | null;
  matchType?: 'vat' | 'similar';
  /** DEV-406: the P.IVA read off the DDT must be checked by the user before it is saved. */
  vatNeedsReview?: true;
  vatReviewReason?: VatReviewReason;
}

/**
 * `invalid-italian-vat`: wrong length/check digit or not a VAT at all.
 */
export type VatReviewReason = 'invalid-italian-vat';

/** Page info of the DDT as printed, merged from all the photos read. */
export interface ExtractedDocumentPages {
  printedCurrent: number | null;
  printedTotal: number | null;
  continues: boolean;
  photosRead: number;
}

export interface ExtractionResult {
  supplier?: {
    name: string | null;
    vatNumber: string | null;
    address: string | null;
    phone: string | null;
    email: string | null;
    isValid: boolean;
  };
  document?: {
    documentNumber: string | null;
    documentDate: string | null;
    isValid: boolean;
    /** DEV-411: absent on drafts extracted before multi-page reading. */
    pages?: ExtractedDocumentPages;
  };
  inferredAllergens?: Array<{
    productName: string;
    allergens: InferredAllergenEntry[];
  }>;
  supplierMatch?: {
    reason: string;
    candidates: ExtractedSupplierCandidate[];
  };
}

export interface DeliveryNote {
  id: string;
  documentNumber: string | null;
  date: string | null;
  receivedAt: string;
  status: 'DRAFT' | 'COMPLETED' | 'ARCHIVED';
  temperature: number | null;
  vehicleHygiene: boolean;
  packagingIntact: boolean;
  notes: string | null;
  supplier: Supplier | null;
  items: DeliveryItem[];
  photos: DeliveryNotePhoto[];
  lots?: MatchingLot[];
  currentStep?: string;
  itemsCount: number;
  allItemsValidated: boolean;
  extractionStatus: ExtractionStatus;
  extractionResult: ExtractionResult | null;
  totalAmount?: number | null;
  currency?: string | null;
}

export interface UpdateDraftDto {
  supplierId?: string;
  documentNumber?: string;
  date?: string;
  temperature?: number | null;
  vehicleHygiene?: boolean;
  packagingIntact?: boolean;
  notes?: string | null;
  currentStep?: string;
  itemsCount?: number;
  allItemsValidated?: boolean;
  items?: Array<{
    id: string;
    storageEquipmentId?: string;
    storageCleaningAreaId?: string;
    storageAssignmentSource?: string;
  }>;
}

export interface CreateDeliveryNoteDto {
  supplierId: string;
  documentNumber: string;
  date: string; // ISO date string
  temperature?: number | null;
  vehicleHygiene?: boolean;
  packagingIntact?: boolean;
  notes?: string | null;
}

export interface AddItemDto {
  productName: string;
  quantity: number;
  unit: string;
  batchNumber: string;
  /** Empty string allowed only when hasNoExpiry === true. */
  expiryDate: string;
  hasNoExpiry?: boolean;

  isDamaged?: boolean;
  isTemperatureNonCompliant?: boolean;
  isHygieneNonCompliant?: boolean;
  allergens?: { allergenId: string; category: 'CONTAINS' | 'MAY_CONTAIN' }[];
  unitPrice?: number;
  lineTotal?: number;
  currency?: string;
}

export interface AddBatchItemDto {
  productName: string;
  quantity: number;
  unit: string;
  batchNumber?: string;
  expiryDate?: string;
  hasNoExpiry?: boolean;

  isDamaged?: boolean;
  isTemperatureNonCompliant?: boolean;
  isHygieneNonCompliant?: boolean;
}

export interface ValidateItemDto {
  batchNumber: string;
  expiryDate?: string | null;
  hasNoExpiry?: boolean;
}

export interface UpdateItemDto {
  productName?: string;
  quantity?: number;
  unit?: string;
  batchNumber?: string | null;
  expiryDate?: string | null;
  hasNoExpiry?: boolean;
  allergens?: { allergenId: string; category: 'CONTAINS' | 'MAY_CONTAIN' }[];
  unitPrice?: number;
  lineTotal?: number;
  currency?: string;
}

export interface UpdateItemStatusDto {
  status: 'ACCEPTED' | 'ACCEPTED_WITH_RESERVATION' | 'REJECTED';
  reason?: string;
}

/** One page of the goods-receiving history. `total` spans the whole date range. */
export interface DeliveryNotesPage {
  items: DeliveryNote[];
  total: number;
  page: number;
  pageSize: number;
}

// Interfaccia API response (allineata al backend)
export interface DeliveryNoteApiResponse {
  id: string;
  tenantId: string;
  documentNumber: string | null;
  date: string | null;
  receivedAt: string;
  supplierId: string | null;
  temperature: number | null;
  vehicleHygiene: boolean;
  packagingIntact: boolean;
  notes: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  supplier: {
    id: string;
    name: string;
    vatNumber: string;
    address?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  items: Array<{
    id: string;
    productName: string;
    batchNumber: string | null;
    expiryDate: string | null;
    /** Server-authored flag. Mutually exclusive with expiryDate via DB CHECK constraint. */
    hasNoExpiry: boolean;
    quantity: number;
    unit: string;
    status: string;
    statusReason: string | null;
    isCompliant: boolean;
    labelPhotos: Array<{
      id: string;
      storageKey: string;
      originalFilename: string;
    }>;
    allergens?: Array<{
      allergenId: string;
      allergenCode: string;
      allergenName: string;
      category: 'CONTAINS' | 'MAY_CONTAIN';
    }>;
    unitPrice?: number | null;
    lineTotal?: number | null;
    currency?: string | null;
    pastPurchaseCount?: number;
    lastPurchaseDate?: string | null;
  }>;
  photos?: Array<{
    id: string;
    storageKey: string;
    originalFilename: string;
    sourceKey?: string | null;
  }>;
  lots?: Array<{
    id: string;
    name: string;
    lotNumber: string;
    notes: string | null;
  }>;
  currentStep?: string | null;
  itemsCount: number;
  allItemsValidated: boolean;
  extractionStatus?: string;
  extractionResult?: unknown;
}

export interface SupplierApiResponse {
  id: string;
  tenantId: string;
  name: string;
  vatNumber: string;
  address: string | null;
  email: string | null;
  phone: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CompleteReceiptLotAllergenDto {
  allergenId: string;
  category: 'CONTAINS' | 'MAY_CONTAIN';
}

export interface CompleteReceiptDto {
  supplierId: string;
  documentNumber: string;
  date: string;
  temperature?: number | null;
  vehicleHygiene?: boolean;
  packagingIntact?: boolean;
  notes?: string | null;
  supplierName?: string;
  items: Array<{
    productName: string;
    quantity: number;
    unit: string;
    batchNumber: string;
    expiryDate?: string;
    isDamaged?: boolean;
    isTemperatureNonCompliant?: boolean;
    isHygieneNonCompliant?: boolean;
    supplierName?: string;
    allergens?: CompleteReceiptLotAllergenDto[];
  }>;
}

export interface DeliveryNoteDuplicateSummary {
  id: string;
  documentNumber: string | null;
  date: string | null;
  supplierName: string | null;
  status: 'DRAFT' | 'COMPLETED' | 'ARCHIVED';
  receivedAt: string;
}

export interface DuplicateCheckResponse {
  duplicates: DeliveryNoteDuplicateSummary[];
}

export interface RecallCheckMatchRecall {
  id: string;
  title: string;
  link: string;
  affectedProducts: string[];
  brands: string[];
  lotNumbers: string[];
  matchLevel: 'POSSIBLE' | 'CONFIRMED';
}

export interface RecallCheckMatch {
  itemId: string;
  productName: string;
  recalls: RecallCheckMatchRecall[];
}

export interface RecallCheckResponse {
  matches: RecallCheckMatch[];
}

export interface IGoodsReceivingService {
  // Fornitori
  getSuppliers(): Promise<Supplier[]>;
  createSupplier(data: CreateSupplierDto): Promise<Supplier>;

  // Prodotti
  getProductNames(): Promise<string[]>;

  // DDT
  findAll(from?: string, to?: string, page?: number): Promise<DeliveryNotesPage>;
  findById(id: string, options?: { includeLots?: boolean }): Promise<DeliveryNote | null>;
  createDeliveryNote(data: CreateDeliveryNoteDto, files: File[]): Promise<DeliveryNote>;
  closeDeliveryNote(id: string): Promise<DeliveryNote>;
  completeReceipt(
    data: CompleteReceiptDto,
    documentPhotos: File[],
    labelPhotosByItem: Map<number, File[]>,
  ): Promise<DeliveryNote>;

  // Estrazione AI (solo per etichette singoli articoli)
  extractItemData(file: File): Promise<{
    productName: string | null;
    quantity: string | null;
    unit: string | null;
    batchNumber: string | null;
    expiryDate: string | null;
    allergens: Array<{ allergenCode: string; category: 'CONTAINS' | 'MAY_CONTAIN' }> | null;
  } | null>;

  // Articoli
  addItem(deliveryNoteId: string, item: AddItemDto, files?: File[]): Promise<DeliveryItem>;
  addBatchItems(deliveryNoteId: string, items: AddBatchItemDto[]): Promise<DeliveryItem[]>;
  deleteItem(itemId: string): Promise<void>;
  updateItem(itemId: string, data: UpdateItemDto): Promise<DeliveryItem>;
  validateItem(itemId: string, data: ValidateItemDto): Promise<DeliveryItem>;
  updateItemStatus(itemId: string, data: UpdateItemStatusDto): Promise<DeliveryItem>;

  // Bozze
  findDrafts(): Promise<DeliveryNote[]>;
  createDraft(): Promise<DeliveryNote>;
  updateDraft(id: string, data: UpdateDraftDto): Promise<DeliveryNote>;
  deleteDraft(id: string): Promise<void>;

  // Foto DDT
  addPhotos(
    deliveryNoteId: string,
    files: File[],
    options?: { extract?: boolean; appendPages?: boolean },
  ): Promise<DeliveryNotePhoto[]>;
  deletePhoto(deliveryNoteId: string, photoId: string): Promise<void>;

  // Duplicate check
  checkDuplicate(deliveryNoteId: string): Promise<DuplicateCheckResponse>;

  // Recall check
  checkRecalls(deliveryNoteId: string): Promise<RecallCheckResponse>;

  // Foto
  uploadLabelPhoto(itemId: string, file: File): Promise<LabelPhoto>;

  // Storage
  getPresignedUrl(storageKey: string): Promise<string>;
}

export interface IGoodsReceivingExtractionService {
  extractItemData(file: File): Promise<{
    productName: string | null;
    quantity: string | null;
    unit: string | null;
    batchNumber: string | null;
    expiryDate: string | null;
    allergens: Array<{ allergenCode: string; category: 'CONTAINS' | 'MAY_CONTAIN' }> | null;
  } | null>;

  suggestExpiryDate(input: SuggestExpiryDateInput): Promise<SuggestExpiryDateResponse>;
}

export interface SuggestExpiryDateInput {
  productName: string;
  quantity?: string;
  unit?: string;
  batchNumber?: string;
  supplierName?: string;
  documentDate?: string;
}

export interface SuggestExpiryDateResponse {
  hasExpiry: boolean;
  suggestedDate: string | null;
  confidence: 'low' | 'medium' | 'high' | null;
  reasoning: string;
}

/** Thrown when the tenant has exhausted the weekly AI extraction quota (HTTP 429). */
export class AiExpirySuggestionLimitError extends Error {
  constructor(message?: string) {
    super(message ?? 'AI extraction weekly limit reached');
    this.name = 'AiExpirySuggestionLimitError';
  }
}
